import { useScroll } from "@sciam/shared";
import { useState } from "react";

import SciAmMeatballIcon from "~/assets/sciam";
import SciAmFullTextIcon from "~/assets/sciam-full-text";
import { handleTrackClick } from "~/lib/analytics/handleTrackClick";
import { cx } from "~utils";
import UserDropdown from "./UserDropdown";

// Promo/Call to Action components
import { useEntitlements } from "~features/access";
import { usePageData } from "~core/hooks/use-page";
import SalesBanner from "./SalesBanner";
import SubscribeLink from "./SubscribeLink";
import { useCta } from "./use-cta";

import styles from "./Header.module.css";

const Logo = ({ full = false, isSponsored = false }) => {
  // fullLogo set to true only on homepage route (/routes/homepage.js),
  // & we * only * render it if we haven't scrolled from top of homepage
  return (
    <a
      href="/"
      aria-label="Scientific American"
      className={cx(
        full ? styles.logoLink__full_text : styles.logoLink,
        isSponsored && styles.logoLink__isSponsored,
      )}
      onClick={(e) => {
        handleTrackClick("site-header-meatball-4", e);
      }}
    >
      <span className="sr-only">Scientific American</span>
      {full ? <SciAmFullTextIcon /> : <SciAmMeatballIcon />}
    </a>
  );
};

/**
 * @param {Object} props
 * @param {import('./use-cta').CallToAction} [props.cta]
 */
const HeaderText = ({ children, cta, showTitle, hasSub }) => {
  if (!showTitle && !hasSub && cta) {
    return (
      <a
        className={styles.headerText}
        href={cta.href}
        onClick={(e) => {
          handleTrackClick(`site-header-${cta.id}`, e);
          cta.onClick?.(e);
        }}
      >
        {cta.text}
      </a>
    );
  }

  // We're in e.g. article and scrolled down: render article title in header
  if (showTitle && children) {
    return <h2 className={styles.headerTitle} dangerouslySetInnerHTML={{ __html: children }}></h2>;
  }

  return null; // useful to reduce re-renders
};

/**
 * @param {Object} props
 * @param {String} props.persistentHeaderTitle
 * @param {Boolean} props.fullLogo
 * @param {Boolean} props.showActivePromo - Should we display current marketing banner on this page
 * @param {Boolean} props.showSubscribeLink - Should we display SubscribeLink (only true for homepage)
 */
function Header({
  persistentHeaderTitle = "",
  fullLogo = false,
  showActivePromo = false,
  showSubscribeLink = false,
}) {
  const { isSponsored, article } = usePageData();
  const [isScrolled, setIsScrolled] = useState(false);
  useScroll(({ y: scrollY }) => setIsScrolled(scrollY > 32));

  // Editorial content shows the title on scroll.
  // Sponsor content shows the advertiser at all times
  const showPersistentTitle = (persistentHeaderTitle && isScrolled) || isSponsored;
  const { hasSub } = useEntitlements();

  const mayShowBanner = showActivePromo && showSubscribeLink;

  const cta = useCta({
    showActivePromo,
    preferActivePromo: mayShowBanner,
  });

  return (
    <header
      className={cx(
        styles.headerContainer,
        isScrolled ? styles.headerContainer__offset : null,
        mayShowBanner && !cta && styles.headerContainer__maybe_banner,
        isSponsored && styles.headerContainer__isSponsored,
      )}
      role="banner"
    >
      {/**
       * this `a` tag is for a11y, so differently-abled users don't have to navigate
       * through all the header contents before they can get to e.g. an article's body
       */}
      <a
        href="#main"
        id="skipToMain"
        className="skiptocontent sr-only-focusable sr-only"
        onClick={(e) => {
          handleTrackClick("site-header-skip-to-main-content-7", e);
        }}
      >
        Skip to main content
      </a>

      {/**
       * Only show sales banner to subsscribers and non-subscribers.  cta prop determines messaging
       */}
      {showSubscribeLink && <SalesBanner cta={cta} positionPrefix="homepage-header" />}

      <div className={styles.header}>
        <div className={styles.left}>
          <Logo full={fullLogo} isSponsored={isSponsored} />
        </div>
        <div className={styles.center}>
          {!showSubscribeLink && (
            <HeaderText cta={cta} showTitle={showPersistentTitle} hasSub={hasSub}>
              {article?.digital_column
                ? article.digital_column + ": " + persistentHeaderTitle
                : persistentHeaderTitle}
            </HeaderText>
          )}
        </div>
        <div className={styles.right}>
          {showSubscribeLink && hasSub === false && (
            <SubscribeLink position="homepage-header-getsciam-button-2" />
          )}
          <UserDropdown />
        </div>
      </div>
    </header>
  );
}

export default Header;
